import { trigger } from '@angular/animations';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { AssetDto, GalleryDto } from '@nusign/picflow-backend-types';
import { FeaturePaywallDialogComponent } from '../../../account/dialogs/feature-paywall-dialog/feature-paywall-dialog.component';
import { fadeUpIfSubtle } from '../../../animations/fade-up-subtle.if.animation';
import { fadeUpIf } from '../../../animations/fade-up.if.animation';
import { billingScopes } from '../../../billing-consts';
import { Rect } from '../../../classes/rect.class';
import { GROWTHBOOK } from '../../../growthbook';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { debounce } from '../../../lib/debounce';
import { GetBounds } from '../../../lib/get-bounds';
import { isMetaKey } from '../../../lib/is-meta-key';
import { AssetFeedbackService } from '../../../services/asset-feedback.service';
import { CommentService } from '../../../services/comment.service';
import { DialogService } from '../../../services/dialog.service';
import { PermissionService } from '../../../services/permission.service';
import { StateService } from '../../../services/state.service';
import { BillingScopePipe } from '../../../shared-extra/pipes/billing-scope.pipe';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { WysiwygDirective } from '../../../wysiwyg/directives/wysiwyg.directive';
import { Shape } from '../../types/shapes';
import { CommentThread } from '../comments/comments.component';
import { EmojiSelectorComponent } from '../lazy/emoji-selector/emoji-selector.component';
import { VideoTimeData } from '../video-viewer/video-viewer.component';

@Component({
  selector: 'app-create-comment-overlay',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './create-comment-overlay.component.html',
  styleUrl: './create-comment-overlay.component.scss',
  animations: [trigger('createCommentOverlay', fadeUpIfSubtle), trigger('messageActions', fadeUpIf)],
  imports: [
    EmojiSelectorComponent,
    WysiwygDirective,
    ButtonDirective,
    SvgComponent,
    ContextMenuComponent,
    TranslatePipe,
    TooltipDirective,
    AvatarComponent,
    BillingScopePipe,
  ],
})
export class CreateCommentOverlayComponent implements AfterViewInit {
  public state = inject(StateService);
  private feedbackService = inject(AssetFeedbackService);
  private commentService = inject(CommentService);
  private permissionService = inject(PermissionService);
  private dialog = inject(DialogService);
  public growthBook = inject(GROWTHBOOK);
  @ViewChild('overlay', { static: true }) overlay: ElementRef<HTMLDivElement>;
  @ViewChild('commentField', { static: true }) commentField: ElementRef<HTMLDivElement>;
  @Input() asset: AssetDto;
  @Input() gallery: GalleryDto;
  @Input() accessLevel: GalleryAccessLevel;
  @Input() isTeamMember: boolean;
  @Input() annotationMode: boolean;
  @Input() videoTime: VideoTimeData;
  @Input() position: Rect;
  @Input() commentMessage: { text: string; addVideoFramePosition: boolean; private: boolean };
  @Input() createAnnotationShapes: Shape[];
  @Input() isDesktop: boolean;
  @Input() commentPlacePosition: Rect;
  @Input() commentPlaceAlignment: 'left' | 'right' | 'center';
  @Input() offsetScale: number;
  @Input() scale: number;
  @Input() offsetX: number;
  @Input() offsetY: number;
  @Input() sideMenuOpen: boolean;
  @Output() toggleAnnotationMode = new EventEmitter<void>();
  @Output() onNewThread = new EventEmitter<CommentThread>();
  @Output() customPosition = new EventEmitter<Rect>();
  @Output() onClose = new EventEmitter<void>();
  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent) {
    const metaKey = isMetaKey(event);
    if (event.target['getAttribute']?.('name') === 'new-thread-from-overlay') {
      if (event.key === 'Enter' && metaKey) this.postNewThread();
    }
  }

  dragging = signal(false);

  async ngAfterViewInit() {
    if (!this.createAnnotationShapes?.length) {
      setTimeout(() => {
        this.commentField.nativeElement.focus();
      });
    }
  }

  mouseDown() {
    this.dragging.set(true);
  }

  mouseMove(event: MouseEvent) {
    if (!this.dragging()) return;
    this.position = this.position.add(new Rect(event.movementX, event.movementY));
    this.reportCustomPosition();
    event.stopPropagation();
  }

  mouseUp() {
    this.dragging.set(false);
  }

  reportCustomPosition = debounce(() => {
    this.customPosition.emit(this.position);
  }, 100);

  addEmoji(emoji: { emoji: { native: string } }) {
    this.commentMessage.text += emoji.emoji.native;
  }

  private calcRelativeCommentPlacePosition() {
    if (!this.commentPlacePosition) return undefined;
    const bounds = GetBounds(
      this.offsetScale,
      this.scale,
      this.sideMenuOpen,
      this.asset,
      this.offsetX,
      this.offsetY,
      this.isDesktop
    );

    return new Rect(
      (1 / bounds.width) * Math.round(this.commentPlacePosition.x - bounds.x),
      (1 / bounds.height) * Math.round(this.commentPlacePosition.y - bounds.y),
      (1 / bounds.width) * Math.round(this.commentPlacePosition.width),
      (1 / bounds.height) * Math.round(this.commentPlacePosition.height)
    );
  }

  postNewThread(): void {
    const text = this.commentMessage.text;
    if (!text && !this.createAnnotationShapes?.length) return;
    const privateComment = this.commentMessage.private;

    this.commentMessage.text = '';
    this.commentMessage.private = false;
    this.feedbackService
      .sendComment(
        this.asset,
        this.gallery,
        { text, private: privateComment },
        this.createAnnotationShapes,
        this.commentService.getCommentPosition(this.asset, this.videoTime, this.calcRelativeCommentPlacePosition())
      )
      .then(thread => {
        if (this.annotationMode) {
          this.toggleAnnotationMode.emit();
        }
        this.onNewThread.emit(thread as CommentThread);
        this.onClose.emit();
      });
  }

  togglePrivate() {
    if (this.permissionService.canDisplayElement(billingScopes.asset.privateComments)) {
      this.commentMessage.private = !this.commentMessage.private;
    } else {
      this.dialog.open(FeaturePaywallDialogComponent, { feature: 'privateComments' });
    }
  }
}
